import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { FormInstruction } from '../../../api/generated';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import i18n from '../../../i18n';
import { SelfServiceInstructionProps } from './InstructionComponent';

export function getFieldErrors(clientErrors: unknown[], serverError?: string): string[] {
  const errors = clientErrors.filter((error): error is string => typeof error === 'string');
  if (serverError) errors.push(serverError);
  return [...new Set(errors)];
}

export default function FormInstructionComponent({
  instruction,
  onReadyStatusChange,
  onInstructionChange,
}: SelfServiceInstructionProps<FormInstruction>) {
  const { title, description, fields } = instruction.parameters;

  const defaultValues = useMemo(() => {
    return fields.reduce(
      (acc, field) => {
        acc[field.key] = field.value || '';
        return acc;
      },
      {} as Record<string, string>
    );
  }, [fields]);

  const form = useForm({
    defaultValues,
  });
  const { Field } = form;

  // external API returns a new instruction (different ID)
  // -> force reset form state, tanstack/react-form update()
  // skips default-value updates when fields are touched (isTouched === true).
  const prevInstructionIdRef = useRef(instruction.instructionId);

  useEffect(() => {
    if (prevInstructionIdRef.current !== instruction.instructionId) {
      prevInstructionIdRef.current = instruction.instructionId;

      const values = fields.reduce(
        (acc, f) => {
          acc[f.key] = f.value || '';
          return acc;
        },
        {} as Record<string, string>
      );
      form.reset(values);
    }
  }, [fields, instruction.instructionId, form]);

  // show errors on every response, not only when the instructionId changes.
  useEffect(() => {
    fields.forEach((f) => {
      if (!f.error?.message) return;

      form.setFieldMeta(f.key, (prev) => ({
        ...prev,
        errorMap: { ...prev.errorMap, onChange: f.error!.message },
        isTouched: true,
      }));
    });
  }, [fields, form]);

  const handleInputChange = (key: string, value: any) => {
    form.setFieldMeta(key, (prev) => ({
      ...prev,
      errorMap: { ...prev.errorMap, onChange: undefined },
    }));
    form.setFieldValue(key, value);

    const updatedInstruction = structuredClone(instruction);
    const field = updatedInstruction.parameters.fields.find((f) => f.key === key);
    if (field) {
      field.value = value;
      delete field.error;
    }

    onInstructionChange?.(updatedInstruction);
  };

  const isFormValid = useMemo(() => {
    return fields.every((field) => {
      if (!field.required) return true;
      return field.value !== undefined && field.value !== null && field.value.trim().length > 0;
    });
  }, [fields]);

  useEffect(() => {
    onReadyStatusChange(isFormValid);
  }, [isFormValid, onReadyStatusChange]);

  return (
    <Stack flexDirection='column' gap={2}>
      {title && <Typography variant='h6'>{title}</Typography>}

      {description && <Typography variant='body1'>{description}</Typography>}

      {fields.map((field) => (
        <Field
          key={field.key}
          name={field.key as any}
          validators={{
            onChange: field.required ? validateNonEmptyString : undefined,
          }}
        >
          {({ state, handleBlur }) => {
            const errors = getFieldErrors(state.meta.errors, field.error?.message);

            if (field.type === 'text') {
              return (
                <TextField
                  label={field.label}
                  variant='filled'
                  fullWidth
                  value={state.value}
                  required={field.required}
                  disabled={!field.editable}
                  error={errors.length > 0}
                  helperText={errors.join(', ')}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  onBlur={handleBlur}
                />
              );
            } else {
              return (
                <Autocomplete
                  value={state.value}
                  disabled={!field.editable}
                  onBlur={handleBlur}
                  onInputChange={(_, newValue) => handleInputChange(field.key, newValue)}
                  onChange={(_, newValue) => handleInputChange(field.key, newValue ?? '')}
                  options={field.options ?? []}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      variant='filled'
                      required={field.required}
                      error={errors.length > 0}
                      helperText={errors.join(', ')}
                    />
                  )}
                  freeSolo
                />
              );
            }
          }}
        </Field>
      ))}
    </Stack>
  );
}

function validateNonEmptyString({ value }: { value: unknown }): string | undefined {
  const v = value as string;
  if (!v || v.trim().length === 0) {
    return i18n.t('selfService.fieldRequired');
  }
}
